/*
 * "You are signed out." — the one line that turns a sudden sign-in form into an answer (D-10 P10).
 *
 * Signing out leaves with a full page load (core/shell/AccountMenu.tsx, settings/SecurityPage.tsx),
 * because the model, the socket and the databases are opened once at boot; a router's location state
 * does not survive that, and a query parameter would stay in the address bar, be bookmarked and be
 * shown again on the next visit. So the fact is left in `sessionStorage`, which is this tab's alone,
 * and the sign-in screen takes it.
 *
 * It is written by `authApi.logout()` and by nothing else: whichever surface signs out, the fact
 * belongs to the route that did it.
 *
 * Two things make the taking harder than it looks, and both were measured on the running product
 * before this was written.
 *
 * **The document that signs out reaches the sign-in screen first.** The application routes itself
 * there the moment the session is gone, a beat before the full page load arrives, so for a few
 * hundred milliseconds the *old* document is showing the form — and it must not spend the fact it
 * has just written, because it is on its way out. It therefore never takes it back.
 *
 * **The screen that does show is rendered more than once.** React's strict mode invokes a state
 * initialiser twice and the route mounts twice on the way in, so a reading that simply removed the
 * value left the reader whose state survived with nothing. The first reader therefore replaces the
 * pending value with a mark of its own document: every later reader of that page load is answered
 * the same, and the next document — a reload, which is not a second sign-out — finds a mark that is
 * not its own, throws it away and shows the bare form.
 */

const KEY = "tb:signedOut"

/** What the sign-out writes: a fact no document has read yet. */
const PENDING = "1"

/** True in the document that signed out, which is the one document the fact is not for. */
let left = false

/**
 * A mark every reader of one document shares and no later document repeats: the moment this document
 * began.
 */
function documentMark(): string {
	return `d${String(Math.trunc(performance.timeOrigin))}`
}

/** Records that this tab has just ended its session. Storage may be unavailable; that is not a failure. */
export function markSignedOut(): void {
	left = true
	try {
		sessionStorage.setItem(KEY, PENDING)
	} catch {
		// A browser with site data switched off simply shows the form without the line.
	}
}

/** True for the sign-in screen that follows a sign-out; see this file's head for both halves. */
export function takeSignedOut(): boolean {
	if (left) return false
	try {
		const found = sessionStorage.getItem(KEY)
		if (found === null) return false
		if (found === PENDING) {
			sessionStorage.setItem(KEY, documentMark())
			return true
		}
		if (found === documentMark()) return true
		sessionStorage.removeItem(KEY)
		return false
	} catch {
		return false
	}
}
